import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { getClientCollection, requestClientCollectionSample } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { Spinner, ErrorBanner } from './ClientDashboardPage';

const GOLD = '#C9A84C';
const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

function emptySampleDraft() {
  return {
    requestedSizes: SIZE_OPTIONS.map((size) => ({ size, quantity: 0 })),
    notes: '',
  };
}

function itemKey(item = {}) {
  return String(item.id || item._id || item.productId || item.lookId || item.title || '');
}

export default function ClientCollectionPage() {
  const { theme: T } = useClientTheme();
  const [activeCollection, setActiveCollection] = useState('All');
  const [lightbox, setLightbox] = useState(null);   // { item, imgIndex }
  const [sampleDrafts, setSampleDrafts] = useState({});
  const [expandedSampleCards, setExpandedSampleCards] = useState({});
  const [sampleSavingId, setSampleSavingId] = useState('');
  const [sampleFeedback, setSampleFeedback] = useState({});

  const { data, isLoading, error, refetch } = useQuery('client-collection', getClientCollection, { refetchOnWindowFocus: false });
  const items = data?.items || [];

  const collections = ['All', ...Array.from(new Set(items.map(i => i.collectionName))).filter(Boolean)];
  const filtered = activeCollection === 'All' ? items : items.filter(i => i.collectionName === activeCollection);

  // Keyboard navigation for lightbox
  const handleKey = useCallback((e) => {
    if (!lightbox) return;
    const imgs = lightbox.item.images || [{ url: lightbox.item.imageUrl }];
    if (e.key === 'ArrowRight') setLightbox(lb => ({ ...lb, imgIndex: Math.min(lb.imgIndex + 1, imgs.length - 1) }));
    if (e.key === 'ArrowLeft')  setLightbox(lb => ({ ...lb, imgIndex: Math.max(lb.imgIndex - 1, 0) }));
    if (e.key === 'Escape')     setLightbox(null);
  }, [lightbox]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const getSampleDraft = (item) => sampleDrafts[itemKey(item)] || emptySampleDraft();

  const toggleSampleCard = (item) => {
    const key = itemKey(item);
    setExpandedSampleCards((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateSampleQuantity = (item, size, quantity) => {
    const key = itemKey(item);
    const safeQuantity = Math.max(0, Math.floor(Number(quantity || 0)));
    setSampleDrafts((prev) => {
      const draft = prev[key] || emptySampleDraft();
      return {
        ...prev,
        [key]: {
          ...draft,
          requestedSizes: draft.requestedSizes.map((row) => (
            row.size === size ? { ...row, quantity: safeQuantity } : row
          )),
        },
      };
    });
  };

  const updateSampleNotes = (item, notes) => {
    const key = itemKey(item);
    setSampleDrafts((prev) => {
      const draft = prev[key] || emptySampleDraft();
      return { ...prev, [key]: { ...draft, notes } };
    });
  };

  const submitSampleRequest = async (item) => {
    const key = itemKey(item);
    const draft = getSampleDraft(item);
    const requestedSizes = draft.requestedSizes.filter((row) => Number(row.quantity || 0) > 0);
    if (!requestedSizes.length) {
      setSampleFeedback((prev) => ({ ...prev, [key]: { type: 'error', text: 'Add quantity to at least one size.' } }));
      return;
    }
    setSampleSavingId(key);
    setSampleFeedback((prev) => ({ ...prev, [key]: null }));
    try {
      await requestClientCollectionSample({
        itemId: item.id || item._id,
        requestedSizes,
        notes: draft.notes,
      });
      setSampleFeedback((prev) => ({ ...prev, [key]: { type: 'success', text: 'Sample request sent.' } }));
      setSampleDrafts((prev) => ({ ...prev, [key]: emptySampleDraft() }));
      setExpandedSampleCards((prev) => ({ ...prev, [key]: false }));
      await refetch();
    } catch (err) {
      setSampleFeedback((prev) => ({ ...prev, [key]: { type: 'error', text: err?.response?.data?.error || err.message || 'Could not send sample request.' } }));
    } finally {
      setSampleSavingId('');
    }
  };

  if (isLoading) return <div style={{ padding: 40 }}><Spinner T={T} /></div>;
  if (error) return <div style={{ padding: 40 }}><ErrorBanner message="Could not load collection." /></div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.bg, color: T.text, fontFamily: T.fontFamily }}>

      {/* Sidebar */}
      <div style={{ width: 160, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: '24px 0' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textMuted, padding: '0 18px', marginBottom: 12 }}>
          Collection
        </div>
        {collections.map(col => (
          <button
            key={col}
            onClick={() => setActiveCollection(col)}
            style={{
              width: '100%', textAlign: 'left', padding: '8px 18px',
              background: activeCollection === col ? (T.id === 'light' ? 'rgba(201,168,76,0.08)' : 'rgba(201,168,76,0.10)') : 'none',
              border: 'none', borderLeft: activeCollection === col ? `2px solid ${GOLD}` : '2px solid transparent',
              color: activeCollection === col ? GOLD : T.textSub,
              fontSize: 13, fontWeight: activeCollection === col ? 600 : 400, cursor: 'pointer',
            }}
          >
            {col}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div style={{ flex: 1, padding: 28 }}>
        {filtered.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: T.textMuted, fontSize: 14 }}>
            No looks shared yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
            {filtered.map(item => {
              const imgs = item.images?.length ? item.images : [{ url: item.imageUrl }];
              const imgCount = imgs.length;
              const draft = getSampleDraft(item);
              const key = itemKey(item);
              const feedback = sampleFeedback[key];
              const selectedCount = draft.requestedSizes.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
              const requestOpen = Boolean(expandedSampleCards[key] || selectedCount > 0 || feedback);
              return (
                <div
                  key={item._id}
                  style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.18)' }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none' }}
                >
                  <div onClick={() => setLightbox({ item, imgIndex: 0 })} style={{ paddingTop: '100%', position: 'relative', background: T.id === 'light' ? '#f0f0f0' : '#111', cursor: 'pointer' }}>
                    <img src={imgs[0].url} alt={item.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    {imgCount > 1 && (
                      <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 5, padding: '3px 8px', fontSize: 10, fontWeight: 700, color: '#fff' }}>
                        {imgCount} photos
                      </div>
                    )}
                  </div>
                  {/* Photo strip */}
                  {imgCount > 1 && (
                    <div style={{ display: 'flex', gap: 2, padding: '4px 4px 0', overflowX: 'hidden' }}>
                      {imgs.slice(0, 5).map((img, i) => (
                        <img key={i} src={img.url} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 3, flexShrink: 0, border: i === 0 ? `1.5px solid ${GOLD}` : `1px solid ${T.border}` }} />
                      ))}
                      {imgCount > 5 && <div style={{ width: 34, height: 34, borderRadius: 3, background: T.id === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: T.textMuted }}>+{imgCount - 5}</div>}
                    </div>
                  )}
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{item.title}</div>
                      {item.sampleRequestStatus && (
                        <span style={{ border: `1px solid ${GOLD}`, borderRadius: 999, color: GOLD, fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', padding: '3px 6px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                          Sample {item.sampleRequestStatus}
                        </span>
                      )}
                    </div>
                    {item.description && <div style={{ fontSize: 12, color: T.textSub, marginTop: 4, lineHeight: 1.4 }}>{item.description}</div>}
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>{item.collectionName}</div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSampleCard(item);
                        }}
                        style={{
                          width: '100%',
                          marginTop: 10,
                          border: `1px solid ${GOLD}`,
                          background: requestOpen ? 'transparent' : GOLD,
                          color: requestOpen ? GOLD : '#111',
                          borderRadius: 7,
                          padding: '8px 10px',
                          fontSize: 11,
                          fontWeight: 850,
                          cursor: 'pointer',
                        }}
                      >
                        {requestOpen ? `Hide Sample Request${selectedCount ? ` · ${selectedCount}` : ''}` : item.sampleRequestStatus ? 'Request More Samples' : 'Request Sample'}
                      </button>
                    {requestOpen && (
                      <div style={{ marginTop: 10, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sizes + Quantities</div>
                          <div style={{ fontSize: 10, color: selectedCount ? GOLD : T.textMuted, fontWeight: 800 }}>{selectedCount} selected</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
                          {draft.requestedSizes.map((row) => (
                            <label key={row.size} style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 800, textAlign: 'center' }}>{row.size}</span>
                              <input
                                type="number"
                                min="0"
                                value={row.quantity}
                                onChange={(e) => updateSampleQuantity(item, row.size, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  border: `1px solid ${Number(row.quantity || 0) > 0 ? GOLD : T.border}`,
                                  background: T.id === 'light' ? 'rgba(255,255,255,0.86)' : 'rgba(255,255,255,0.04)',
                                  color: T.text,
                                  borderRadius: 6,
                                  padding: '7px 4px',
                                  fontSize: 12,
                                  fontWeight: 750,
                                  textAlign: 'center',
                                }}
                              />
                            </label>
                          ))}
                        </div>
                        <textarea
                          value={draft.notes}
                          onChange={(e) => updateSampleNotes(item, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Sample notes"
                          rows={2}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            marginTop: 8,
                            border: `1px solid ${T.border}`,
                            background: T.id === 'light' ? 'rgba(255,255,255,0.86)' : 'rgba(255,255,255,0.04)',
                            color: T.text,
                            borderRadius: 7,
                            padding: 8,
                            fontSize: 11,
                            resize: 'vertical',
                            fontFamily: T.fontFamily,
                          }}
                        />
                        {feedback && (
                          <div style={{ marginTop: 7, color: feedback.type === 'error' ? '#ef4444' : GOLD, fontSize: 11, fontWeight: 700 }}>
                            {feedback.text}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            submitSampleRequest(item);
                          }}
                          disabled={sampleSavingId === key}
                          style={{
                            width: '100%',
                            marginTop: 8,
                            border: `1px solid ${GOLD}`,
                            background: selectedCount > 0 ? GOLD : 'transparent',
                            color: selectedCount > 0 ? '#111' : GOLD,
                            borderRadius: 7,
                            padding: '8px 10px',
                            fontSize: 11,
                            fontWeight: 850,
                            cursor: sampleSavingId === key ? 'wait' : 'pointer',
                            opacity: sampleSavingId === key ? 0.72 : 1,
                          }}
                        >
                          {sampleSavingId === key ? 'Sending...' : 'Send Sample Request'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox gallery */}
      {lightbox && (() => {
        const imgs = lightbox.item.images?.length ? lightbox.item.images : [{ url: lightbox.item.imageUrl }];
        const idx = lightbox.imgIndex;
        const total = imgs.length;
        return (
          <div
            onClick={() => setLightbox(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}
          >
            <div onClick={e => e.stopPropagation()} style={{ maxWidth: 760, width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Main image */}
              <div style={{ position: 'relative' }}>
                <img
                  src={imgs[idx].url}
                  alt={lightbox.item.title}
                  style={{ width: '100%', display: 'block', maxHeight: '60vh', objectFit: 'contain', borderRadius: 10, background: '#0a0a0a' }}
                />
                {total > 1 && (
                  <>
                    <button
                      onClick={() => setLightbox(lb => ({ ...lb, imgIndex: Math.max(lb.imgIndex - 1, 0) }))}
                      disabled={idx === 0}
                      style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: 16, cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >‹</button>
                    <button
                      onClick={() => setLightbox(lb => ({ ...lb, imgIndex: Math.min(lb.imgIndex + 1, total - 1) }))}
                      disabled={idx === total - 1}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: 16, cursor: idx === total - 1 ? 'not-allowed' : 'pointer', opacity: idx === total - 1 ? 0.3 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >›</button>
                  </>
                )}
                <button
                  onClick={() => setLightbox(null)}
                  style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >×</button>
              </div>

              {/* Thumbnail strip */}
              {total > 1 && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {imgs.map((img, i) => (
                    <img
                      key={i}
                      src={img.url}
                      alt=""
                      onClick={() => setLightbox(lb => ({ ...lb, imgIndex: i }))}
                      style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: i === idx ? `2px solid ${GOLD}` : '2px solid transparent', opacity: i === idx ? 1 : 0.55, transition: 'opacity 0.15s, border-color 0.15s' }}
                    />
                  ))}
                </div>
              )}

              {/* Caption */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>{lightbox.item.title}</div>
                {lightbox.item.description && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>{lightbox.item.description}</div>}
                {total > 1 && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>{idx + 1} / {total}</div>}
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
